import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Save, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProducts = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const emptyProduct = {
    title: '',
    description: '',
    icon: 'Refrigerator',
    products: [],
    order: 0,
    is_active: true
  };
  
  const [formData, setFormData] = useState(emptyProduct);
  const [productsText, setProductsText] = useState('');

  const iconOptions = ['Refrigerator', 'Tv', 'Smartphone', 'Wind', 'WashingMachine', 'Microwave', 'Monitor'];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Ürünler yüklenemedi');
    }
    setLoading(false);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData(product);
    const productsStr = product.products?.map(p => `${p.name}|${p.details || ''}`).join('\n') || '';
    setProductsText(productsStr);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingProduct(null);
    setFormData({ ...emptyProduct, order: products.length });
    setProductsText('');
    setIsDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Bu ürün kategorisini silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ürün kategorisi silindi');
      fetchProducts();
    } catch (error) {
      toast.error('Ürün kategorisi silinemedi');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const productsArray = productsText.split('\n').filter(line => line.trim()).map(line => {
      const [name, details] = line.split('|');
      return { name: name?.trim() || '', details: details?.trim() || '' };
    });

    const data = {
      ...formData,
      products: productsArray
    };

    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ürün kategorisi güncellendi');
      } else {
        await axios.post(`${API_URL}/api/products`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ürün kategorisi oluşturuldu');
      }
      setIsDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ürün Kategorileri</h1>
          <p className="text-slate-600">Satın aldığınız ürün kategorilerini yönetin.</p>
        </div>
        <Button onClick={handleAdd} className="bg-[#002FA7] hover:bg-[#001d6e]" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kategori
        </Button>
      </div>

      <div className="space-y-4">
        {products.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-600">
              Henüz ürün kategorisi eklenmemiş.
            </CardContent>
          </Card>
        ) : (
          products.map((product, index) => (
            <Card key={product.id} data-testid={`product-item-${index}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <GripVertical className="w-5 h-5 text-slate-400 cursor-move" />
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{product.title}</h3>
                  <p className="text-sm text-slate-600">{product.products?.length || 0} ürün</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(product)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(product.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Kategori Düzenle' : 'Yeni Kategori'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Kategori Adı</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="product-title-input"
                />
              </div>
              <div>
                <Label htmlFor="icon">İkon</Label>
                <select
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full border rounded-md p-2"
                >
                  {iconOptions.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="product-description-input"
              />
            </div>

            <div>
              <Label htmlFor="products">Ürünler (Her satıra: Ürün Adı|Detay)</Label>
              <Textarea
                id="products"
                value={productsText}
                onChange={(e) => setProductsText(e.target.value)}
                rows={6}
                placeholder="Buzdolabı|Tek kapı, çift kapı, no-frost&#10;Çamaşır Makinesi|Tüm marka ve modeller"
                data-testid="products-list-input"
              />
            </div>

            <div>
              <Label htmlFor="order">Sıra</Label>
              <Input
                id="order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#001d6e]" data-testid="save-product-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
